import type { SingularKeys } from '@libs/i18n';

import { TenantProfileDocumentStatus } from '@libs/constants';

import type { TenantProfileDocumentDto } from '../../api/types';

/**
 * Finer-grained, user-facing status used across the Document Hub (status pill,
 * filter tabs, count strip). It maps onto the backend's unified
 * `TenantProfileDocumentStatus`, renaming `invalid` to the design's
 * "Irrelevant".
 */
export type HubDisplayStatus =
  | 'classifying'
  | 'ready_to_extract'
  | 'extracting'
  | 'pending_review'
  | 'done'
  | 'failed'
  | 'irrelevant';

/** Visual tone — maps to MUI palette paths usable directly in `sx`. */
export type StatusTone = 'amber' | 'blue' | 'green' | 'red' | 'grey';

export interface ToneSx {
  fg: string;
  bg: string;
  border: string;
}

export const TONE_SX: Record<StatusTone, ToneSx> = {
  amber: { fg: 'yellow.800', bg: 'yellow.100', border: 'yellow.300' },
  blue: { fg: 'blue.700', bg: 'blue.100', border: 'blue.200' },
  green: { fg: 'green.700', bg: 'green.100', border: 'green.200' },
  red: { fg: 'error.main', bg: 'red.100', border: 'red.200' },
  grey: { fg: 'text.secondary', bg: 'grey.200', border: 'grey.400' },
};

interface StatusMeta {
  labelKey: SingularKeys;
  tone: StatusTone;
}

export const STATUS_META: Record<HubDisplayStatus, StatusMeta> = {
  classifying: { labelKey: 'app.documentHub.status.classifying', tone: 'blue' },
  ready_to_extract: {
    labelKey: 'app.documentHub.status.readyToExtract',
    tone: 'grey',
  },
  extracting: { labelKey: 'app.documentHub.status.extracting', tone: 'blue' },
  pending_review: {
    labelKey: 'app.documentHub.status.pendingReview',
    tone: 'amber',
  },
  done: { labelKey: 'app.documentHub.status.done', tone: 'green' },
  failed: { labelKey: 'app.documentHub.status.failed', tone: 'red' },
  irrelevant: { labelKey: 'app.documentHub.status.irrelevant', tone: 'amber' },
};

/**
 * Collapse a document's unified status into the Hub's display status. Mirrors
 * `doc.status` directly; an extracting document (queued or running) is always
 * shown as `extracting`.
 */
export function deriveDisplayStatus(
  doc: TenantProfileDocumentDto,
): HubDisplayStatus {
  switch (doc.status) {
    case TenantProfileDocumentStatus.DONE:
      return 'done';
    case TenantProfileDocumentStatus.FAILED:
      return 'failed';
    case TenantProfileDocumentStatus.INVALID:
      return 'irrelevant';
    case TenantProfileDocumentStatus.CLASSIFYING:
      return 'classifying';
    case TenantProfileDocumentStatus.READY_TO_EXTRACT:
      return 'ready_to_extract';
    case TenantProfileDocumentStatus.PENDING_REVIEW:
      return 'pending_review';
    case TenantProfileDocumentStatus.EXTRACTING:
      return 'extracting';
    default:
      return 'classifying';
  }
}
